import { useState, useEffect } from "react";
import axios from "axios";
import { FaTrash, FaStar, FaRegEdit } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PageHeading from "../PageHeading";
import { useAuth } from "../../utils/AuthContext";
import { RiDeleteBin6Line } from "react-icons/ri";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import TableSkeleton from "../TableSkeleton";
import ConfirmActionModal from "../ConfirmDeleteModal";

const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";
const MAX_FILE_SIZE = 5 * 1024 * 1024;
const PLACEHOLDER_IMAGE = "https://placehold.co/100";

const AddReviews = () => {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [formData, setFormData] = useState({
    name: user?.name || "",
    role: user?.role || "",
    image: user?.profilePhoto || "",
    rating: 0,
    reviewText: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    setLoading(true);
    setError(null);
    try {
      const source = axios.CancelToken.source();
      const timeout = setTimeout(() => source.cancel("Request timed out"), 10000);
      // console.log("Fetching Reviews from https://api.muktihospital.com/api/reviews");
      const response = await axios.get("https://api.muktihospital.com/api/reviews", {
        headers: { "x-api-key": API_KEY },
        cancelToken: source.token,
      });
      
      clearTimeout(timeout);
      // console.log("Fetch reviews response:", response.data);
      const data = Array.isArray(response.data) ? response.data.map(review => ({
        ...review,
        image: review.image ? `${review.image}?t=${Date.now()}` : null, // Add cache-busting
      })) : [];
      setReviews(data);
      setError(null);
    } catch (error) {
      if (axios.isCancel(error)) {
        setError("Review fetch request timed out");
        toast.error("Review fetch request timed out", { position: "top-right", autoClose: 3000 });
      } else {
        const errorMessage = error.response?.data?.error || error.message || "Failed to fetch reviews.";
        console.error("Fetch Reviews Error:", error);
        setError(errorMessage);
        toast.error(errorMessage, { position: "top-right", autoClose: 3000 });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleStarClick = (starValue) => {
    setFormData({ ...formData, rating: starValue });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.size > MAX_FILE_SIZE) {
      toast.error("File size exceeds 5MB limit");
      return;
    }
    setImageFile(file);

    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setImagePreview(formData.image ? `https://api.muktihospital.com${formData.image}` : null);
    }
  };

  const handleRemoveImage = () => {
    setImagePreview(null);
    setImageFile(null);
    setFormData({ ...formData, image: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setIsSubmitting(true);

    // Validate form data
    if (!formData.name || !formData.role || !formData.reviewText) {
      toast.error("Name, role, and review text are required");
      setLoading(false);
      setIsSubmitting(false);
      return;
    }
    if (formData.rating < 1 || formData.rating > 5) {
      toast.error("Rating must be between 1 and 5");
      setLoading(false);
      setIsSubmitting(false);
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append("name", formData.name);
    formDataToSend.append("role", formData.role);
    formDataToSend.append("rating", formData.rating);
    formDataToSend.append("reviewText", formData.reviewText);
    formDataToSend.append("userId", user.id);
    if (imageFile) {
      formDataToSend.append("image", imageFile);
    } else if (editingId && !formData.image) {
      formDataToSend.append("image", ""); // Clear image if removed
    }

    try {
      const source = axios.CancelToken.source();
      const timeout = setTimeout(() => source.cancel("Request timed out"), 10000);
      const response = editingId
        ? await axios.put(
            `https://api.muktihospital.com/api/reviews/${editingId}`,
            formDataToSend,
            {
              headers: {
                "Content-Type": "multipart/form-data",
                "x-api-key": API_KEY,
              },
              cancelToken: source.token,
            }
          )
        : await axios.post(
            "https://api.muktihospital.com/api/reviews",
            formDataToSend,
            {
              headers: {
                "Content-Type": "multipart/form-data",
                "x-api-key": API_KEY,
              },
              cancelToken: source.token,
            }
          );
      clearTimeout(timeout);
      // console.log("Save review response:", response.data);
      toast.success(editingId ? "Review updated successfully!" : "Review added successfully!", {
        position: "top-right",
        autoClose: 3000,
      });
      fetchReviews();
      setFormData({
        name: user?.name || "",
        role: user?.role || "",
        image: user?.profilePhoto || "",
        rating: 0,
        reviewText: "",
      });
      setImageFile(null);
      setImagePreview(null);
      setEditingId(null);
      setCurrentPage(1);
    } catch (error) {
      if (axios.isCancel(error)) {
        toast.error("Review save request timed out", { position: "top-right", autoClose: 3000 });
      } else {
        const errorMessage = error.response?.data?.error || "Failed to save review";
        console.error("Error saving review:", error.response?.data || error.message);
        toast.error(errorMessage, { position: "top-right", autoClose: 3000 });
      }
    } finally {
      setLoading(false);
      setIsSubmitting(false);
    }
  };

  const confirmDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const handleDelete = async (id) => {
    setLoading(true);
    try {
      const source = axios.CancelToken.source();
      const timeout = setTimeout(() => source.cancel("Request timed out"), 10000);
      await axios.delete(`https://api.muktihospital.com/api/reviews/${id}`, {
        headers: { "x-api-key": API_KEY },
        cancelToken: source.token,
      });
      clearTimeout(timeout);
      toast.success("Review deleted successfully!", { position: "top-right", autoClose: 3000 });
      fetchReviews();
      if (reviews.length <= itemsPerPage * (currentPage - 1) + 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }
    } catch (error) {
      if (axios.isCancel(error)) {
        toast.error("Review delete request timed out", { position: "top-right", autoClose: 3000 });
      } else {
        console.error("Error deleting review:", error.response?.data || error.message);
        toast.error(error.response?.data?.error || "Error deleting review", {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (review) => {
    // console.log("Editing review:", review);
    setEditingId(review.id);
    setFormData({
      name: review.name || user?.name || "",
      role: review.role || user?.role || "",
      image: review.image?.replace(/\?t=\d+/, "") || "", // Remove cache-busting for form
      rating: review.rating || 0,
      reviewText: review.reviewText || "",
    });
    setImageFile(null);
    setImagePreview(review.image ? `https://api.muktihospital.com${review.image.replace(/\?t=\d+/, "")}` : null);
    // console.log("Set editingId:", review.id, "formData:", {
    //   name: review.name,
    //   role: review.role,
    //   image: review.image,
    //   rating: review.rating,
    //   reviewText: review.reviewText,
    // });
  };

  const renderStars = (rating, clickable = false) => {
    return (
      <div className="flex space-x-1 text-yellow-500 cursor-pointer">
        {Array.from({ length: 5 }, (_, index) => (
          <FaStar
            key={index}
            onClick={clickable ? () => handleStarClick(index + 1) : null}
            className={index < rating ? "text-yellow-500" : "text-gray-300"}
          />
        ))}
      </div>
    );
  };

  const totalPages = Math.ceil(reviews.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentReviews = reviews.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const breadcrumbs = [
    { label: "Dashboard", url: "/" },
    { label: "Manage Patients"},
    { label: "Add New Review" },
  ];

  const skeletonColumns = [
    { width: "48px", height: "48px", isCircle: true, className: "p-4", skeletonClassName: "" },
    { width: "150px", height: "16px", className: "p-4", skeletonClassName: "" },
    { width: "100px", height: "16px", className: "p-4", skeletonClassName: "" },
    { width: "200px", height: "16px", className: "p-4", skeletonClassName: "" },
    { width: "60px", height: "16px", className: "p-4 text-right", skeletonClassName: "ml-auto" },
  ];

  if (error) {
    return (
      <div className="text-center p-4 text-red-600">
        {error}
        <button
          onClick={() => fetchReviews()}
          className="ml-4 bg-M-primary-color/90 text-sm text-white py-1 px-3 rounded-md hover:bg-M-primary-color"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
      <PageHeading title="Add New Review" breadcrumbs={breadcrumbs} />
      <div>
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {Array(itemsPerPage).fill(0).map((_, index) => (
              <div key={index} className="bg-white p-4 rounded-lg shadow-md">
                <TableSkeleton
                  rowCount={1}
                  columns={skeletonColumns}
                  baseColor="#e5e7eb"
                  highlightColor="#f3f4f6"
                />
              </div>
            ))}
          </div>
        ) : reviews.length === 0 ? (
          <div className="text-center p-4 text-gray-600">No reviews found</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {currentReviews.map((review) => (
              <div key={review.id} className="bg-white p-4 rounded-lg shadow-md">
                <div className="flex items-center gap-2">
                  <img
                    src={review.image ? `https://api.muktihospital.com${review.image}` : PLACEHOLDER_IMAGE}
                    alt={review.name}
                    className="w-12 h-12 rounded-full"
                    onError={(e) => (e.target.src = PLACEHOLDER_IMAGE)}
                  />
                  <div>
                    <h3 className="font-bold">{review.name}</h3>
                    <p className="text-sm text-gray-600">{review.role}</p>
                  </div>
                </div>
                <div className="mt-2">{renderStars(review.rating)}</div>
                <p className="mt-2 text-sm">{review.reviewText}</p>
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => handleEdit(review)}
                    className="size-7 rounded-full bg-M-Green-color/10 text-M-Green-color text-sm inline-flex items-center justify-center hover:bg-M-Green-color hover:text-white transition-all duration-300"
                    disabled={loading}
                  >
                    <FaRegEdit />
                  </button>
                  <button
                    onClick={() => confirmDelete(review.id)}
                    className="size-7 rounded-full bg-M-secondary-color/10 text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
                    disabled={loading}
                  >
                    <RiDeleteBin6Line />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="p-5 text-end">
          <ul className="inline-flex items-center justify-end border-collapse border border-M-text-color/20 rounded-md overflow-hidden">
            <li>
              <button
                className="table-pagination-item disabled:opacity-50"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || loading}
              >
                <MdKeyboardArrowLeft />
              </button>
            </li>
            {[...Array(totalPages)].map((_, i) => (
              <li key={i}>
                <button
                  className={`table-pagination-item ${currentPage === i + 1 ? "bg-M-primary-color text-white" : ""}`}
                  onClick={() => handlePageChange(i + 1)}
                  disabled={loading}
                >
                  {i + 1}
                </button>
              </li>
            ))}
            <li>
              <button
                className="table-pagination-item"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || loading}
              >
                <MdKeyboardArrowRight />
              </button>
            </li>
          </ul>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="bg-white shadow-sm rounded-lg mt-5">
          <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
              {editingId ? "Edit Review" : "Add Review"}
            </h2>
          </div>
          <div className="p-5">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
              <div>
                <label className="label">Name</label>
                <input
                  type="text"
                  name="name"
                  placeholder="Name"
                  value={formData.name}
                  onChange={handleChange}
                  className="input-field"
                  required
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <label className="label">Role</label>
                <input
                  type="text"
                  name="role"
                  placeholder="Role (e.g., Patient)"
                  value={formData.role}
                  onChange={handleChange}
                  className="input-field"
                  required
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <label className="label">Review Description</label>
                <textarea
                  name="reviewText"
                  placeholder="Review text"
                  value={formData.reviewText}
                  onChange={handleChange}
                  className="input-field"
                  required
                  disabled={isSubmitting}
                ></textarea>
              </div>
              <div>
                <label className="label">Rating</label>
                {renderStars(formData.rating, true)}
              </div>
              <div className="md:col-span-2">
                <label className="label">Profile Image</label>
                {(imagePreview || formData.image) && (
                  <div className="mb-4 relative inline-block border shadow rounded-md p-2">
                    <img
                      src={imagePreview || `https://api.muktihospital.com${formData.image}`}
                      alt="Preview"
                      className="w-24 h-24 object-cover"
                      onError={(e) => (e.target.src = PLACEHOLDER_IMAGE)}
                    />
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 text-xs"
                      title="Remove image"
                      disabled={isSubmitting}
                    >
                      <FaTrash size={10} />
                    </button>
                  </div>
                )}
                <div className="mt-2">
                  <input
                    type="file"
                    accept="image/*"
                    className="border p-2 rounded w-full"
                    disabled={isSubmitting}
                    onChange={handleFileChange}
                  />
                </div>
              </div>
            </div>

            <div className="mt-6 flex gap-4 justify-start">
              <button
                type="submit"
                className="bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color transition-all duration-200"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : editingId ? "Update Review" : "Add Review"}
              </button>
              {editingId && (
                <button
                  type="button"
                  onClick={() => {
                    setEditingId(null);
                    setFormData({
                      name: user?.name || "",
                      role: user?.role || "",
                      image: user?.profilePhoto || "",
                      rating: 0,
                      reviewText: "",
                    });
                    setImageFile(null);
                    setImagePreview(null);
                  }}
                  className="bg-[#E7633D]/90 text-sm text-white py-2 px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300"
                  disabled={isSubmitting}
                >
                  Cancel Edit
                </button>
              )}
            </div>
          </div>
        </div>
      </form>

      <ConfirmActionModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        ids={deleteId}
        entityName="review"
        title="Confirm Deletion"
        message="Are you sure you want to delete this review?"
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
};

export default AddReviews;